import React, { useState, useEffect } from 'react';
import api from '../../../services/api';
import './CalendarSyncSettings.css';

const CalendarSyncSettings = () => {
  const [syncSettings, setSyncSettings] = useState([]);
  const [syncLog, setSyncLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState({});
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showLog, setShowLog] = useState(false);

  useEffect(() => {
    loadSyncSettings();
    loadSyncLog();
  }, []);

  const loadSyncSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/portal/calendar/sync/settings');
      setSyncSettings(response.data);
      setError(null);
    } catch (err) {
      console.error('Error loading sync settings:', err);
      setError('Failed to load sync settings');
    } finally {
      setLoading(false);
    }
  };

  const loadSyncLog = async () => {
    try {
      const response = await api.get('/portal/calendar/sync/log?limit=20');
      setSyncLog(response.data);
    } catch (err) {
      console.error('Error loading sync log:', err);
    }
  };

  const connectCalendar = async (provider) => {
    try {
      const endpoint = provider === 'google'
        ? '/portal/calendar/google/connect'
        : '/portal/calendar/outlook/connect';

      const response = await api.get(endpoint);

      if (response.data.authUrl) {
        // Open OAuth window
        const width = 600;
        const height = 700;
        const left = window.screen.width / 2 - width / 2;
        const top = window.screen.height / 2 - height / 2;

        window.open(
          response.data.authUrl,
          'Calendar OAuth',
          `width=${width},height=${height},left=${left},top=${top}`
        );

        // Listen for OAuth completion
        const handleMessage = (event) => {
          if (event.data.type === 'calendar_sync_success') {
            setSuccess(`${provider === 'google' ? 'Google' : 'Outlook'} Calendar connected successfully`);
            loadSyncSettings();
            window.removeEventListener('message', handleMessage);
          }
        };

        window.addEventListener('message', handleMessage);
      }
    } catch (err) {
      console.error(`Error connecting ${provider}:`, err);
      setError(`Failed to connect ${provider === 'google' ? 'Google' : 'Outlook'} Calendar`);
    }
  };

  const disconnectCalendar = async (provider) => {
    if (!window.confirm(`Are you sure you want to disconnect ${provider === 'google' ? 'Google' : 'Outlook'} Calendar?`)) {
      return;
    }

    try {
      const endpoint = provider === 'google'
        ? '/portal/calendar/google/disconnect'
        : '/portal/calendar/outlook/disconnect';

      await api.delete(endpoint);
      setSuccess(`${provider === 'google' ? 'Google' : 'Outlook'} Calendar disconnected`);
      loadSyncSettings();
    } catch (err) {
      console.error(`Error disconnecting ${provider}:`, err);
      setError(`Failed to disconnect ${provider === 'google' ? 'Google' : 'Outlook'} Calendar`);
    }
  };

  const syncCalendar = async (provider) => {
    try {
      setSyncing({ ...syncing, [provider]: true });

      const endpoint = provider === 'google'
        ? '/portal/calendar/google/sync'
        : '/portal/calendar/outlook/sync';

      const response = await api.post(endpoint);
      setSuccess(`${provider === 'google' ? 'Google' : 'Outlook'} Calendar synced successfully`);
      loadSyncSettings();
      loadSyncLog();
    } catch (err) {
      console.error(`Error syncing ${provider}:`, err);
      setError(`Failed to sync ${provider === 'google' ? 'Google' : 'Outlook'} Calendar`);
    } finally {
      setSyncing({ ...syncing, [provider]: false });
    }
  };

  const syncAllCalendars = async () => {
    try {
      setSyncing({ all: true });
      await api.post('/portal/calendar/sync/all');
      setSuccess('All calendars synced successfully');
      loadSyncSettings();
      loadSyncLog();
    } catch (err) {
      console.error('Error syncing all calendars:', err);
      setError('Failed to sync all calendars');
    } finally {
      setSyncing({ all: false });
    }
  };

  const updateSyncSettings = async (provider, settings) => {
    try {
      await api.put('/portal/calendar/sync/settings', {
        provider,
        ...settings
      });
      setSuccess('Sync settings updated');
      loadSyncSettings();
    } catch (err) {
      console.error('Error updating sync settings:', err);
      setError('Failed to update sync settings');
    }
  };

  const getProviderSettings = (provider) => {
    return syncSettings.find(s => s.provider === provider);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      success: { class: 'status-success', text: 'Success' },
      failed: { class: 'status-failed', text: 'Failed' },
      partial: { class: 'status-partial', text: 'Partial' }
    };

    const statusInfo = statusMap[status] || { class: 'status-unknown', text: status };
    return <span className={`status-badge ${statusInfo.class}`}>{statusInfo.text}</span>;
  };

  const CalendarProviderCard = ({ provider, displayName, icon }) => {
    const settings = getProviderSettings(provider);
    const isConnected = settings && settings.is_enabled;

    return (
      <div className="calendar-provider-card">
        <div className="provider-header">
          <div className="provider-info">
            <i className={`provider-icon ${icon}`}></i>
            <h3>{displayName}</h3>
          </div>
          {isConnected && (
            <span className="connection-status connected">
              <i className="fas fa-check-circle"></i> Connected
            </span>
          )}
        </div>

        {isConnected ? (
          <div className="provider-details">
            <div className="sync-info">
              <p><strong>Email:</strong> {settings.provider_email}</p>
              <p><strong>Last Sync:</strong> {formatDate(settings.last_sync_at)}</p>
              <p><strong>Status:</strong> {getStatusBadge(settings.last_sync_status)}</p>
              {settings.last_sync_error && (
                <p className="sync-error"><strong>Last Error:</strong> {settings.last_sync_error}</p>
              )}
            </div>

            <div className="sync-settings">
              <h4>Sync Settings</h4>
              <div className="settings-grid">
                <div className="setting-item">
                  <label>Sync Direction</label>
                  <select
                    value={settings.sync_direction || 'bidirectional'}
                    onChange={(e) => updateSyncSettings(provider, { sync_direction: e.target.value })}
                  >
                    <option value="bidirectional">Bidirectional</option>
                    <option value="to_external">To {displayName} Only</option>
                    <option value="from_external">From {displayName} Only</option>
                  </select>
                </div>
                <div className="setting-item">
                  <label>Sync Past Days</label>
                  <input
                    type="number"
                    value={settings.sync_past_days || 30}
                    onChange={(e) => updateSyncSettings(provider, { sync_past_days: parseInt(e.target.value) })}
                    min="1"
                    max="365"
                  />
                </div>
                <div className="setting-item">
                  <label>Sync Future Days</label>
                  <input
                    type="number"
                    value={settings.sync_future_days || 90}
                    onChange={(e) => updateSyncSettings(provider, { sync_future_days: parseInt(e.target.value) })}
                    min="1"
                    max="365"
                  />
                </div>
              </div>
            </div>

            <div className="provider-actions">
              <button
                onClick={() => syncCalendar(provider)}
                disabled={syncing[provider]}
                className="btn btn-primary"
              >
                {syncing[provider] ? (
                  <><i className="fas fa-spinner fa-spin"></i> Syncing...</>
                ) : (
                  <><i className="fas fa-sync"></i> Sync Now</>
                )}
              </button>
              <button
                onClick={() => disconnectCalendar(provider)}
                className="btn btn-danger"
              >
                <i className="fas fa-unlink"></i> Disconnect
              </button>
            </div>
          </div>
        ) : (
          <div className="provider-connect">
            <p>Connect your {displayName} to sync calendar events automatically.</p>
            <button
              onClick={() => connectCalendar(provider)}
              className="btn btn-success"
            >
              <i className="fas fa-link"></i> Connect {displayName}
            </button>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="calendar-sync-settings">
        <div className="loading">
          <i className="fas fa-spinner fa-spin"></i> Loading sync settings...
        </div>
      </div>
    );
  }

  return (
    <div className="calendar-sync-settings">
      <div className="settings-header">
        <h2><i className="fas fa-calendar-alt"></i> Calendar Sync Settings</h2>
        <div className="header-actions">
          <button
            onClick={syncAllCalendars}
            disabled={syncing.all || syncSettings.length === 0}
            className="btn btn-primary"
          >
            {syncing.all ? (
              <><i className="fas fa-spinner fa-spin"></i> Syncing All...</>
            ) : (
              <><i className="fas fa-sync-alt"></i> Sync All</>
            )}
          </button>
          <button
            onClick={() => setShowLog(!showLog)}
            className="btn btn-secondary"
          >
            <i className="fas fa-history"></i> {showLog ? 'Hide Log' : 'Show Log'}
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-error">
          <i className="fas fa-exclamation-circle"></i>
          {error}
          <button onClick={() => setError(null)} className="alert-close">&times;</button>
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          <i className="fas fa-check-circle"></i>
          {success}
          <button onClick={() => setSuccess(null)} className="alert-close">&times;</button>
        </div>
      )}

      <div className="providers-grid">
        <CalendarProviderCard
          provider="google"
          displayName="Google Calendar"
          icon="fab fa-google"
        />
        <CalendarProviderCard
          provider="outlook"
          displayName="Outlook Calendar"
          icon="fab fa-microsoft"
        />
      </div>

      {showLog && (
        <div className="sync-log">
          <h3><i className="fas fa-history"></i> Sync History</h3>
          {syncLog.length === 0 ? (
            <p className="no-data">No sync history available</p>
          ) : (
            <div className="log-table-wrapper">
              <table className="log-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Provider</th>
                    <th>Action</th>
                    <th>Status</th>
                    <th>Processed</th>
                    <th>Created</th>
                    <th>Updated</th>
                    <th>Failed</th>
                  </tr>
                </thead>
                <tbody>
                  {syncLog.map((log, index) => (
                    <tr key={index}>
                      <td>{formatDate(log.created_at)}</td>
                      <td>
                        <i className={`fab fa-${log.provider === 'google' ? 'google' : 'microsoft'}`}></i>
                        {' '}{log.provider}
                      </td>
                      <td>{log.action}</td>
                      <td>{getStatusBadge(log.status)}</td>
                      <td>{log.events_processed || 0}</td>
                      <td>{log.events_created || 0}</td>
                      <td>{log.events_updated || 0}</td>
                      <td>{log.events_failed || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <div className="sync-info-footer">
        <div className="info-box">
          <i className="fas fa-info-circle"></i>
          <div>
            <h4>About Calendar Sync</h4>
            <ul>
              <li>Events are synced based on your configured date range</li>
              <li>Bidirectional sync keeps both calendars in sync</li>
              <li>Manual sync can be triggered at any time</li>
              <li>Automatic sync runs periodically in the background</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalendarSyncSettings;
